import styles from './DealTracker.module.css';

import type { FC } from 'react';
import type { DealChain } from 'src/utils/dealChain';
import type { TrackerStep, TrackerNote, DealTrackerModel } from 'src/utils/dealTracker';

import React from 'react';
import classNames from 'classnames';

import { formatDateTime } from 'src/utils/date';
import { dealTxLink, shortenHash } from 'src/utils/dealChain';

import { Stack } from 'src/components/themed/Stack';
import { Typography } from 'src/components/themed/Typography';

/**
 * The deal tracker (contracts v3, UI part §3) — built the way postal and parcel trackers are,
 * because that is the pattern people already know how to read.
 *
 * What it borrows, and why each part is here rather than "nice to have":
 *
 * - **A header that answers "where is it" and "when".** NN/g's status-tracker guidelines put the
 *   current state first and in plain words; Baymard's order-tracking research found the missing
 *   expected date to be the single biggest cause of distrust. So the top line is the status in the
 *   buyer's language — "Payment secured in escrow", never `escrowed` — and the second is the date
 *   that matters now: the logist's ETA, the deadline of an open window, or what we are waiting for.
 * - **Few big steps, past ones stamped.** A completed step shows the date and time it was
 *   confirmed, the current one is accented and says what is awaited *and from whom*, and future
 *   steps are dimmed with no time against them — a guessed future date is worse than none.
 * - **Detail lives inside the step.** Amounts, the transaction that carried a step, the second
 *   signature of a dispute: notes under their own milestone rather than a separate log.
 * - **No silent money.** Every movement is a line with an amount and, where a transaction carried
 *   it, a link to that transaction on the chain's explorer.
 *
 * Everything it renders is a fact api recorded (`deal_step_events`): on-chain steps carry block
 * time and a hash, platform steps carry server time. The component derives nothing from a status.
 */

interface DealTrackerProps {
  tracker: DealTrackerModel;
  /** Needed only for the explorer links; the tracker renders fully without it. */
  chain: DealChain | null;
}

const TONE_CLASS = {
  progress: styles.toneProgress,
  success: styles.toneSuccess,
  danger: styles.toneDanger,
  neutral: styles.toneNeutral,
};

export const DealTracker: FC<DealTrackerProps> = ({ tracker, chain }) => (
  <Stack gap={0} className={styles.card}>
    <Stack gap={1} className={styles.headline}>
      <Typography variant="title3" fontWeight="600" className={TONE_CLASS[tracker.headline.tone]}>
        {tracker.headline.status}
      </Typography>
      {tracker.headline.detail ? (
        <Typography color="lightWhite" variant="small">
          {tracker.headline.detail}
        </Typography>
      ) : null}
    </Stack>

    <ol className={styles.steps}>
      {tracker.steps.map((step) => (
        <Step key={step.key} step={step} chain={chain} />
      ))}
    </ol>
  </Stack>
);

const Step: FC<{ step: TrackerStep; chain: DealChain | null }> = ({ step, chain }) => (
  <li className={classNames(styles.step, step.state === 'future' && styles.future)}>
    <span
      className={classNames(
        styles.marker,
        step.state === 'done' && styles.markerDone,
        step.state === 'current' && styles.markerCurrent,
        step.state === 'exception' && styles.markerException
      )}
    />

    <Stack gap={0}>
      <Typography color="white" variant="small" fontWeight={step.state === 'current' ? '600' : undefined}>
        {step.title}
      </Typography>

      {/* Past steps are stamped; the current one says what it is waiting for; future ones say
          neither, because a date we do not have is not a date. */}
      {step.at ? (
        <Typography color="lightWhite" variant="small">
          {formatDateTime(step.at)}
        </Typography>
      ) : null}

      {step.waitingFor ? (
        <Typography color="lightWhite" variant="small">
          {step.waitingFor}
        </Typography>
      ) : null}

      {/* The description says what the step *was*, in the past tense, so it belongs only to steps
          that have happened. Printing it under the one we are still waiting for put "Waiting for
          the dealer to confirm" directly above "The dealer accepted and quoted the delivery."

          A `done` step without a time is a real case rather than an oversight: on a chain whose
          poller harvests no events (the legacy TRON rentals) the deal's own mirrored flags prove
          a step happened while nothing records *when*. Saying what happened without inventing a
          timestamp is the honest half of that. */}
      {(step.at || step.state === 'done') && !step.waitingFor ? (
        <Typography color="lightWhite" variant="small">
          {step.description}
        </Typography>
      ) : null}

      {step.txHash ? <TxLink chain={chain} txHash={step.txHash} /> : null}

      {step.notes.length > 0 ? (
        <Stack gap={0} className={styles.notes}>
          {step.notes.map((note, index) => (
            <Note key={`${step.key}-${index}`} note={note} chain={chain} />
          ))}
        </Stack>
      ) : null}
    </Stack>
  </li>
);

const Note: FC<{ note: TrackerNote; chain: DealChain | null }> = ({ note, chain }) => (
  <Stack gap={0}>
    <Typography color="white" variant="small">
      {note.label}
    </Typography>
    {note.at ? (
      <Typography color="lightWhite" variant="small">
        {formatDateTime(note.at)}
      </Typography>
    ) : null}
    {note.txHash ? <TxLink chain={chain} txHash={note.txHash} /> : null}
  </Stack>
);

const TxLink: FC<{ chain: DealChain | null; txHash: string }> = ({ chain, txHash }) => {
  const href = dealTxLink(chain, txHash);

  return (
    <Typography color="lightWhite" variant="small">
      {href ? (
        <a className={styles.link} href={href} target="_blank" rel="noreferrer">
          {shortenHash(txHash)}
        </a>
      ) : (
        shortenHash(txHash)
      )}
    </Typography>
  );
};
